import type { DocumentType } from '@/types/verification';

export type VerificationStatusField =
  | 'huru_background_check_status'
  | 'huru_clearance_status'
  | 'cipc_registration_status'
  | 'sars_clearance_status'
  | 'pirb_registration_status'
  | 'nhbrc_certificate_status'
  | 'saqcc_gas_practitioner_status'
  | 'electrical_coc_status'
  | 'gas_coc_status'
  | 'plumbing_coc_status'
  | 'electric_fence_coc_status'
  | 'water_installation_certificate_status'
  | 'public_liability_insurance_status'
  | 'qualification_certificate_status'
  | 'tgcsa_certificate_status'
  | 'ffc_certificate_status'
  | 'fsp_license_status'
  | 'sars_pr_number_status'
  | 'red_seal_certificate_status'
  | 'repssa_registration_status'
  | 'electrical_contractor_registration_status'
  | 'id_document_status'
  | 'coa_certificate_status'
  | 'ems_fire_safety_certificate_status'
  | 'liquor_license_status'
  | 'food_handler_certificate_status'
  | 'cleaning_chemicals_approval_status'
  | 'pest_control_certificate_status'
  | 'iso_22000_certificate_status'
  | 'fssc_22000_certificate_status'
  | 'sole_prop_business_proof_status'
  | 'property_control_proof_status';

const LEGACY_DOCUMENT_TYPE_ALIASES: Partial<Record<DocumentType, DocumentType[]>> = {
  SARS_TAX_COMPLIANCE: ['SARS_CLEARANCE'],
};

const DOCUMENT_TYPE_NORMALIZATION_MAP: Record<string, DocumentType> = {
  HURU_BACKGROUND_CHECK: 'HURU_BACKGROUND_CHECK',
  HURU_CLEARANCE: 'HURU_CLEARANCE',
  CIPC_REGISTRATION: 'CIPC_REGISTRATION',
  SARS_TAX_COMPLIANCE: 'SARS_TAX_COMPLIANCE',
  SARS_CLEARANCE: 'SARS_TAX_COMPLIANCE',
  PIRB_REGISTRATION: 'PIRB_REGISTRATION',
  NHBRC_CERTIFICATE: 'NHBRC_CERTIFICATE',
  SAQCC_GAS_PRACTITIONER: 'SAQCC_GAS_PRACTITIONER',
  ELECTRICAL_COC: 'ELECTRICAL_COC',
  GAS_COC: 'GAS_COC',
  PLUMBING_COC: 'PLUMBING_COC',
  ELECTRIC_FENCE_COC: 'ELECTRIC_FENCE_COC',
  WATER_INSTALLATION_CERTIFICATE: 'WATER_INSTALLATION_CERTIFICATE',
  PUBLIC_LIABILITY_INSURANCE: 'PUBLIC_LIABILITY_INSURANCE',
  QUALIFICATION_CERTIFICATE: 'QUALIFICATION_CERTIFICATE',
  TGCSA_CERTIFICATE: 'TGCSA_CERTIFICATE',
  FFC_CERTIFICATE: 'FFC_CERTIFICATE',
  FSP_LICENSE: 'FSP_LICENSE',
  SARS_PRACTITIONER_REGISTRATION: 'SARS_PRACTITIONER_REGISTRATION',
  RED_SEAL_CERTIFICATE: 'RED_SEAL_CERTIFICATE',
  REPSSA_REGISTRATION: 'REPSSA_REGISTRATION',
  ELECTRICAL_CONTRACTOR_REGISTRATION: 'ELECTRICAL_CONTRACTOR_REGISTRATION',
  ID_DOCUMENT: 'ID_DOCUMENT',
  COA_CERTIFICATE: 'COA_CERTIFICATE',
  EMS_FIRE_SAFETY_CERTIFICATE: 'EMS_FIRE_SAFETY_CERTIFICATE',
  LIQUOR_LICENSE: 'LIQUOR_LICENSE',
  FOOD_HANDLER_CERTIFICATE: 'FOOD_HANDLER_CERTIFICATE',
  CLEANING_CHEMICALS_APPROVAL: 'CLEANING_CHEMICALS_APPROVAL',
  PEST_CONTROL_CERTIFICATE: 'PEST_CONTROL_CERTIFICATE',
  ISO_22000_CERTIFICATE: 'ISO_22000_CERTIFICATE',
  FSSC_22000_CERTIFICATE: 'FSSC_22000_CERTIFICATE',
  SOLE_PROP_BUSINESS_PROOF: 'SOLE_PROP_BUSINESS_PROOF',
  PROPERTY_CONTROL_PROOF: 'PROPERTY_CONTROL_PROOF',
  OTHER: 'OTHER',
};

const DOCUMENT_TYPE_TO_STATUS_FIELD: Partial<Record<DocumentType, VerificationStatusField>> = {
  HURU_BACKGROUND_CHECK: 'huru_background_check_status',
  HURU_CLEARANCE: 'huru_clearance_status',
  CIPC_REGISTRATION: 'cipc_registration_status',
  SARS_TAX_COMPLIANCE: 'sars_clearance_status',
  SARS_CLEARANCE: 'sars_clearance_status',
  PIRB_REGISTRATION: 'pirb_registration_status',
  NHBRC_CERTIFICATE: 'nhbrc_certificate_status',
  SAQCC_GAS_PRACTITIONER: 'saqcc_gas_practitioner_status',
  ELECTRICAL_COC: 'electrical_coc_status',
  GAS_COC: 'gas_coc_status',
  PLUMBING_COC: 'plumbing_coc_status',
  ELECTRIC_FENCE_COC: 'electric_fence_coc_status',
  WATER_INSTALLATION_CERTIFICATE: 'water_installation_certificate_status',
  PUBLIC_LIABILITY_INSURANCE: 'public_liability_insurance_status',
  QUALIFICATION_CERTIFICATE: 'qualification_certificate_status',
  TGCSA_CERTIFICATE: 'tgcsa_certificate_status',
  FFC_CERTIFICATE: 'ffc_certificate_status',
  FSP_LICENSE: 'fsp_license_status',
  SARS_PRACTITIONER_REGISTRATION: 'sars_pr_number_status',
  RED_SEAL_CERTIFICATE: 'red_seal_certificate_status',
  REPSSA_REGISTRATION: 'repssa_registration_status',
  ELECTRICAL_CONTRACTOR_REGISTRATION: 'electrical_contractor_registration_status',
  ID_DOCUMENT: 'id_document_status',
  COA_CERTIFICATE: 'coa_certificate_status',
  // --- Restaurant-specific additions ---
  EMS_FIRE_SAFETY_CERTIFICATE: 'ems_fire_safety_certificate_status',
  LIQUOR_LICENSE: 'liquor_license_status',
  FOOD_HANDLER_CERTIFICATE: 'food_handler_certificate_status',
  CLEANING_CHEMICALS_APPROVAL: 'cleaning_chemicals_approval_status',
  PEST_CONTROL_CERTIFICATE: 'pest_control_certificate_status',
  ISO_22000_CERTIFICATE: 'iso_22000_certificate_status',
FSSC_22000_CERTIFICATE: 'fssc_22000_certificate_status',
  SOLE_PROP_BUSINESS_PROOF: 'sole_prop_business_proof_status',
  /**
   * Stay only. Deliberately separate from the business-existence fields above:
   * this asks whether the host may let THIS property, which is a different
   * question from whether their business exists.
   */
  PROPERTY_CONTROL_PROOF: 'property_control_proof_status',
};

export const getNormalizedDocumentType = (rawDocumentType: string | null | undefined): DocumentType | null => {
  if (typeof rawDocumentType !== 'string') {
    return null;
  }

  const normalized = rawDocumentType.trim().toUpperCase();
  return DOCUMENT_TYPE_NORMALIZATION_MAP[normalized] ?? null;
};

export const getDocumentTypeAuditKeys = (documentType: DocumentType): DocumentType[] => {
  const aliases = LEGACY_DOCUMENT_TYPE_ALIASES[documentType] || [];
  return [documentType, ...aliases];
};

/**
 * Every status field the verification system can write, derived from the mapping
 * above.
 *
 * Exported so counting, ranking and display can agree with the registry instead
 * of each carrying a hand-maintained copy that drifts the moment a document type
 * is added.
 *
 * Deduplicated because two document types can legitimately write the same field -
 * `SARS_CLEARANCE` is a legacy alias of `SARS_TAX_COMPLIANCE`. Left as-is, a
 * verified SARS status would be counted twice by anything iterating this.
 */
export const DOCUMENT_STATUS_FIELDS: ReadonlyArray<VerificationStatusField> = Object.freeze(
  Array.from(
    new Set(
      Object.values(DOCUMENT_TYPE_TO_STATUS_FIELD).filter(
        (field): field is VerificationStatusField => typeof field === 'string',
      ),
    ),
  ),
);

export const getVerificationStatusFieldForDocumentType = (
  documentType: DocumentType,
): VerificationStatusField | null => {
  return DOCUMENT_TYPE_TO_STATUS_FIELD[documentType] ?? null;
};

export const getAuditorKeyForDocumentType = (documentType: DocumentType): DocumentType => {
  return getNormalizedDocumentType(documentType) ?? documentType;
};
